/**
 * Drata public API payloads → SourceRecord (spec section 6). Controls are primary rows;
 * monitors, monitor failures and evidence are related children. isReady true → PASS, false → FAIL,
 * missing → UNKNOWN. Readiness, monitoring, evidence and scope remain distinct facets (see
 * extractDrataControlFacets) so a ready control never implies every attached resource passes.
 */
import { canonicalRecordKey } from '../../ids.js';
import type { Classification, Link, SourceRecord } from '../../providers/records.js';
import type {
  DrataCheckResultStatus,
  DrataControl,
  DrataEvidence,
  DrataFramework,
  DrataMonitor,
  DrataMonitorFailure,
  DrataPersonnel,
  DrataWorkspace,
} from '../../providers/drata.js';
import { DrataControlSchema } from '../../providers/drata.js';
import { DRATA_EMPLOYMENT_STATUSES, DRATA_REAL_EMPLOYMENT_STATUSES } from '../../providers/drata.js';
import type { SeverityLevel, StandardStatus, Tri } from '../../status.js';
import { triFromNullable } from '../../status.js';
import { applyClassificationRules, DRATA_CLASSIFICATION_RULES } from './classificationRules.js';
import {
  buildRecord,
  dedupeAndSort,
  emptyResult,
  linkChildren,
  makeKey,
  safeLink,
  type NormalizeContext,
  type NormalizeResult,
  type NormalizeWarning,
  type ParentChildRelation,
  controlTitle,
} from './common.js';
import { dedupeRelations } from './vanta.js';
import { computeFrameworkStatusFromRecords } from '../frameworks.js';

// ---------------------------------------------------------------------------------------------
// Status mapping
// ---------------------------------------------------------------------------------------------

/** isReady: true → PASS, false → FAIL, null/undefined → UNKNOWN (never PASS). */
export function mapDrataReadiness(isReady: boolean | null | undefined): StandardStatus {
  if (isReady === true) return 'PASS';
  if (isReady === false) return 'FAIL';
  return 'UNKNOWN';
}

export function drataReadinessRaw(isReady: boolean | null | undefined): string | null {
  if (typeof isReady !== 'boolean') return null;
  return `isReady=${isReady}`;
}

const MONITOR_STATUS: Record<DrataCheckResultStatus, StandardStatus> = {
  PASSED: 'PASS',
  FAILED: 'FAIL',
  ERROR: 'UNKNOWN',
  PENDING: 'UNKNOWN',
  UNUSED: 'NOT_APPLICABLE',
  // real-providers change: verified V2 values; neither is a pass.
  READY: 'UNKNOWN',
  PREAUDIT: 'UNKNOWN',
};

export function mapDrataMonitorStatus(raw: string | null | undefined): StandardStatus {
  if (!raw) return 'UNKNOWN';
  return (MONITOR_STATUS as Record<string, StandardStatus>)[raw] ?? 'UNKNOWN';
}

export function mapDrataEvidenceStatus(raw: string | null | undefined): StandardStatus {
  switch (raw) {
    case 'CURRENT':
      return 'PASS';
    case 'EXPIRED':
      return 'FAIL';
    case 'EXPIRING_SOON':
      return 'DUE_SOON';
    case 'NEEDS_REVIEW':
      return 'NEEDS_ATTENTION';
    // real-providers change: verified V2 `/evidence` statuses (PROVIDER_API_NOTES D9).
    case 'READY':
      return 'PASS';
    case 'NEEDS_ARTIFACT':
    case 'NEEDS_SOURCE':
      return 'FAIL';
    case 'NEEDS_ATTENTION':
    case 'ERROR':
      return 'NEEDS_ATTENTION';
    default:
      return 'UNKNOWN';
  }
}

export function mapDrataSeverity(raw: string | null | undefined): SeverityLevel | null {
  switch (raw) {
    case 'CRITICAL':
      return 'critical';
    case 'HIGH':
      return 'high';
    case 'MEDIUM':
      return 'medium';
    case 'LOW':
      return 'low';
    default:
      return null;
  }
}

/** Drata personnel check: PASS/FAIL/UNKNOWN; absent means the tenant does not report it. */
export function mapDrataCheck(raw: string | null | undefined): StandardStatus {
  if (raw === 'PASS') return 'PASS';
  if (raw === 'FAIL') return 'FAIL';
  return 'UNKNOWN';
}

// ---------------------------------------------------------------------------------------------
// Facets kept distinct from status
// ---------------------------------------------------------------------------------------------

export interface DrataControlFacets {
  isReady: Tri;
  isMonitored: Tri;
  hasEvidence: Tri;
  inScope: Tri;
  archived: boolean;
}

/** Read the distinct control facets from the retained raw payload (spec section 6). */
export function extractDrataControlFacets(record: SourceRecord): DrataControlFacets | null {
  if (record.provider !== 'drata' || record.objectType !== 'control') return null;
  const parsed = DrataControlSchema.safeParse(record.raw);
  if (!parsed.success) return null;
  const c = parsed.data;
  return {
    isReady: triFromNullable(c.isReady),
    isMonitored: triFromNullable(c.isMonitored),
    hasEvidence: triFromNullable(c.hasEvidence),
    inScope: triFromNullable(c.inScope),
    archived: c.archivedAt != null,
  };
}

function classify(objectType: SourceRecord['objectType'], name: string, opts: { description?: string | null; category?: string | null } = {}): Classification {
  return applyClassificationRules(DRATA_CLASSIFICATION_RULES, { provider: 'drata', objectType, name, ...opts }).classification;
}

function primaryParent(ids: readonly string[]): string | null {
  if (ids.length === 0) return null;
  return [...ids].sort()[0] ?? null;
}

// ---------------------------------------------------------------------------------------------
// Per-object normalizers
// ---------------------------------------------------------------------------------------------

export function normalizeDrataWorkspace(ws: DrataWorkspace, ctx: NormalizeContext): SourceRecord {
  return buildRecord(ctx, {
    provider: 'drata',
    objectType: 'workspace',
    sourceId: ws.id,
    canonicalKey: null,
    title: ws.name,
    description: ws.slug ?? null,
    rawStatus: ws.isDefault ? 'DEFAULT' : null,
    status: 'NOT_APPLICABLE',
    deactivated: false,
    classification: { isPolicy: false, isInfrastructure: false, isEmployeeCompliance: false, isCompanyCompliance: false },
    frameworkIds: ws.frameworkIds ?? [],
    sourceUpdatedAt: ws.createdAt ?? null,
    raw: ws,
  });
}

export function normalizeDrataControl(control: DrataControl, ctx: NormalizeContext, warnings: NormalizeWarning[] = []): SourceRecord {
  const links: Link[] = [];
  const link = safeLink('Open in Drata', control.controlUrl, 'drata', ctx);
  if (link) links.push(link);
  else if (control.controlUrl) warnings.push({ code: 'invalid_link', objectType: 'control', sourceId: control.id, message: 'control link rejected' });
  const archived = control.archivedAt != null;
  if (!archived && control.inScope !== false && (control.isReady === null || control.isReady === undefined)) {
    warnings.push({ code: 'missing_field', objectType: 'control', sourceId: control.id, message: 'isReady missing; status UNKNOWN' });
  }
  return buildRecord(ctx, {
    provider: 'drata',
    objectType: 'control',
    sourceId: control.id,
    canonicalKey: canonicalRecordKey('drata', 'control', control.code),
    title: controlTitle(control.code, control.name),
    description: control.description ?? null,
    rawStatus: drataReadinessRaw(control.isReady),
    status: archived ? 'DEACTIVATED' : control.inScope === false ? 'NOT_APPLICABLE' : mapDrataReadiness(control.isReady),
    deactivated: archived,
    classification: classify('control', control.name, { description: control.description }),
    frameworkIds: control.frameworks.map((f) => f.id),
    links,
    sourceUpdatedAt: control.updatedAt ?? null,
    raw: control,
  });
}

export function normalizeDrataMonitor(monitor: DrataMonitor, ctx: NormalizeContext, warnings: NormalizeWarning[] = []): SourceRecord {
  const links: Link[] = [];
  const link = safeLink('Open monitor', monitor.monitorUrl, 'drata', ctx);
  if (link) links.push(link);
  else if (monitor.monitorUrl) warnings.push({ code: 'invalid_link', objectType: 'monitor', sourceId: monitor.id, message: 'monitor link rejected' });
  const disabled = monitor.isEnabled === false;
  const parentId = primaryParent(monitor.controlIds);
  return buildRecord(ctx, {
    provider: 'drata',
    objectType: 'monitor',
    sourceId: monitor.id,
    // Drata monitor ids are catalog identifiers shared by every tenant.
    canonicalKey: canonicalRecordKey('drata', 'monitor', monitor.id),
    title: monitor.name,
    description: monitor.description ?? null,
    rawStatus: monitor.checkResultStatus,
    status: disabled ? 'DEACTIVATED' : mapDrataMonitorStatus(monitor.checkResultStatus),
    deactivated: disabled,
    classification: classify('monitor', monitor.name, { description: monitor.description, category: monitor.checkType }),
    parentKey: parentId ? makeKey(ctx, 'drata', 'control', parentId) : null,
    links,
    sourceUpdatedAt: monitor.lastCheck ?? null,
    raw: monitor,
  });
}

export function drataFailureSourceId(failure: DrataMonitorFailure, monitorId: string): string {
  return failure.id ?? `${monitorId}|${failure.resourceId}|${failure.packageName ?? ''}`;
}

export function normalizeDrataMonitorFailure(failure: DrataMonitorFailure, monitorId: string, ctx: NormalizeContext): SourceRecord {
  // real-providers change: V2 failures carry `status` OPEN | EXCLUDED; excluded findings are deactivated.
  const excluded = failure.status === 'EXCLUDED';
  return buildRecord(ctx, {
    provider: 'drata',
    objectType: 'monitor_failure',
    sourceId: drataFailureSourceId(failure, monitorId),
    canonicalKey: null,
    title: failure.resourceName,
    description: failure.reason,
    rawStatus: failure.status ?? 'FAILED',
    status: excluded ? 'DEACTIVATED' : 'FAIL',
    severity: mapDrataSeverity(failure.severity),
    deactivated: excluded,
    classification: classify('monitor_failure', failure.resourceName, { category: failure.resourceType }),
    parentKey: makeKey(ctx, 'drata', 'monitor', monitorId),
    sourceUpdatedAt: failure.firstFailedAt ?? null,
    raw: { ...failure, monitorId: failure.monitorId ?? monitorId },
  });
}

export function normalizeDrataEvidence(evidence: DrataEvidence, ctx: NormalizeContext, warnings: NormalizeWarning[] = []): SourceRecord {
  const links: Link[] = [];
  const link = safeLink('Open evidence', evidence.evidenceUrl, 'drata', ctx);
  if (link) links.push(link);
  else if (evidence.evidenceUrl) warnings.push({ code: 'invalid_link', objectType: 'evidence', sourceId: evidence.id, message: 'evidence link rejected' });
  const parentId = primaryParent(evidence.controlIds);
  return buildRecord(ctx, {
    provider: 'drata',
    objectType: 'evidence',
    sourceId: evidence.id,
    canonicalKey: null,
    title: evidence.name,
    description: evidence.description ?? null,
    rawStatus: evidence.status,
    status: mapDrataEvidenceStatus(evidence.status),
    deactivated: false,
    classification: classify('evidence', evidence.name, { category: evidence.type }),
    parentKey: parentId ? makeKey(ctx, 'drata', 'control', parentId) : null,
    links,
    sourceUpdatedAt: evidence.updatedAt ?? null,
    raw: evidence,
  });
}

export function normalizeDrataFramework(fw: DrataFramework, ctx: NormalizeContext): SourceRecord {
  const ready = fw.controlsReady ?? null;
  const total = fw.controlsTotal ?? null;
  return buildRecord(ctx, {
    provider: 'drata',
    objectType: 'framework',
    sourceId: fw.id,
    canonicalKey: canonicalRecordKey('drata', 'framework', fw.id),
    title: fw.name,
    description: fw.description ?? null,
    rawStatus: total === null ? null : `${ready ?? '?'}/${total}`,
    status: total === null || ready === null ? 'UNKNOWN' : total > 0 && ready >= total ? 'PASS' : 'NEEDS_ATTENTION',
    deactivated: fw.isEnabled === false,
    classification: { isPolicy: false, isInfrastructure: false, isEmployeeCompliance: false, isCompanyCompliance: true },
    frameworkIds: [fw.id],
    raw: fw,
  });
}

/** real-providers change: legacy plus verified V2 employment values; anything else maps to UNKNOWN. */
const KNOWN_DRATA_EMPLOYMENT: ReadonlySet<string> = new Set<string>([...DRATA_EMPLOYMENT_STATUSES, ...DRATA_REAL_EMPLOYMENT_STATUSES]);

export function drataPersonName(p: DrataPersonnel): string {
  const name = [p.firstName, p.lastName].filter((s): s is string => Boolean(s && s.trim())).join(' ').trim();
  return name || p.email || p.id;
}

export function normalizeDrataPersonnel(p: DrataPersonnel, ctx: NormalizeContext): SourceRecord {
  const checks = p.complianceChecks ?? null;
  const values = checks ? Object.values(checks).filter((v): v is 'PASS' | 'FAIL' | 'UNKNOWN' => v === 'PASS' || v === 'FAIL' || v === 'UNKNOWN') : [];
  // real-providers change: verified V2 adds SPECIAL_FORMER_* (former) and UNKNOWN (stays UNKNOWN).
  const former = p.employmentStatus === 'FORMER_EMPLOYEE' || p.employmentStatus === 'FORMER_CONTRACTOR' || p.employmentStatus === 'SPECIAL_FORMER_EMPLOYEE' || p.employmentStatus === 'SPECIAL_FORMER_CONTRACTOR';
  const notInScope = p.employmentStatus === 'OUT_OF_SCOPE' || p.employmentStatus === 'SERVICE_ACCOUNT';
  // An employment status the contract does not list (or the verified `UNKNOWN`) cannot derive a pass from the checks.
  const unknownEmployment = p.employmentStatus === 'UNKNOWN' || !KNOWN_DRATA_EMPLOYMENT.has(p.employmentStatus ?? '');
  let status: StandardStatus;
  if (former) status = 'DEACTIVATED';
  else if (notInScope) status = 'NOT_APPLICABLE';
  else if (unknownEmployment) status = 'UNKNOWN';
  else if (values.length === 0) status = 'UNKNOWN';
  else if (values.includes('FAIL')) status = 'FAIL';
  else if (values.every((v) => v === 'PASS')) status = 'PASS';
  else status = 'UNKNOWN';
  return buildRecord(ctx, {
    provider: 'drata',
    objectType: 'person',
    sourceId: p.id,
    canonicalKey: null,
    title: drataPersonName(p),
    description: p.email,
    rawStatus: p.employmentStatus,
    status,
    deactivated: former,
    classification: { isPolicy: false, isInfrastructure: false, isEmployeeCompliance: true, isCompanyCompliance: false },
    sourceUpdatedAt: p.startDate ?? null,
    raw: p,
  });
}

// ---------------------------------------------------------------------------------------------
// Payload-level normalization
// ---------------------------------------------------------------------------------------------

export interface DrataPayload {
  workspaces?: readonly DrataWorkspace[];
  controls?: readonly DrataControl[];
  monitors?: readonly DrataMonitor[];
  /** Failures grouped by the monitor they were fetched under. */
  monitorFailures?: readonly { monitorId: string; failures: readonly DrataMonitorFailure[] }[];
  evidence?: readonly DrataEvidence[];
  frameworks?: readonly DrataFramework[];
  personnel?: readonly DrataPersonnel[];
}

export function normalizeDrataPayload(payload: DrataPayload, ctx: NormalizeContext): NormalizeResult {
  const result = emptyResult(DRATA_CLASSIFICATION_RULES.version);
  const { warnings } = result;
  const records: SourceRecord[] = [];
  const relations: ParentChildRelation[] = [];

  for (const ws of payload.workspaces ?? []) records.push(normalizeDrataWorkspace(ws, ctx));
  for (const c of payload.controls ?? []) {
    const rec = normalizeDrataControl(c, ctx, warnings);
    records.push(rec);
    for (const monitorId of c.monitorIds) relations.push({ parentKey: rec.recordKey, childKey: makeKey(ctx, 'drata', 'monitor', monitorId), kind: 'monitor' });
    for (const evidenceId of c.evidenceIds) relations.push({ parentKey: rec.recordKey, childKey: makeKey(ctx, 'drata', 'evidence', evidenceId), kind: 'evidence' });
  }
  for (const m of payload.monitors ?? []) {
    const rec = normalizeDrataMonitor(m, ctx, warnings);
    records.push(rec);
    for (const controlId of m.controlIds) relations.push({ parentKey: makeKey(ctx, 'drata', 'control', controlId), childKey: rec.recordKey, kind: 'monitor' });
  }
  for (const group of payload.monitorFailures ?? []) {
    for (const f of group.failures) {
      const rec = normalizeDrataMonitorFailure(f, group.monitorId, ctx);
      records.push(rec);
      relations.push({ parentKey: makeKey(ctx, 'drata', 'monitor', group.monitorId), childKey: rec.recordKey, kind: 'failure' });
    }
  }
  for (const e of payload.evidence ?? []) {
    const rec = normalizeDrataEvidence(e, ctx, warnings);
    records.push(rec);
    for (const controlId of e.controlIds) relations.push({ parentKey: makeKey(ctx, 'drata', 'control', controlId), childKey: rec.recordKey, kind: 'evidence' });
  }
  for (const f of payload.frameworks ?? []) records.push(normalizeDrataFramework(f, ctx));
  for (const p of payload.personnel ?? []) records.push(normalizeDrataPersonnel(p, ctx));

  result.records = linkChildren(dedupeAndSort(records, warnings), warnings);
  result.relations = dedupeRelations(relations);
  for (const framework of payload.frameworks ?? []) {
    if (framework.numInScopeControls == null) continue;
    const controls = computeFrameworkStatusFromRecords(framework.id, framework.name, result.records, ['controls']).metrics[0]!;
    if (controls.denominator !== null && controls.denominator !== framework.numInScopeControls) {
      warnings.push({ code: 'count_mismatch', objectType: 'framework', sourceId: framework.id, message: `Computed ${controls.denominator} applicable controls; Drata reports numInScopeControls=${framework.numInScopeControls}. Check per-framework exclusions.` });
    }
  }
  return result;
}
